import {LitElement, html, nothing} from 'lit';
import {BopisController} from 'custom-extensions/bopis';
import styles from './MyStore.component.scss?as=stylesheet';

/* Although this is "My Store", the "Your Store" is displayed as that's the main call to action now */

export default class MyStore extends LitElement {

	/**
	 * Define customized shadow root options.
	 * @see https://lit.dev/docs/components/shadow-dom/#setting-shadowrootoptions
	 * @static
	 */
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: false,
		mode: 'closed'
	};

	static get styles () {
		return [styles];
	}

	/**
	 * @constructor
	 */
	constructor () {
		super();
		this.controller = BopisController;
		this.modalID = 'js-bopis-mystore';
		this.modal = document.getElementById(this.modalID);

		this.ariaHasPopup = 'dialog';
		this.ariaExpanded = false;
		this.ariaLabel = 'Select Your Store';
		this.setAttribute('aria-controls', this.modalID);
		this.isSingleStoreSite = window.mivaJS.Is_Single_Store_Site ?? false;
		this.singleStoreUrl = window.mivaJS.Single_Store_Url ?? null;
	}

	/**
	 * Invoked when a component is added to the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#connectedcallback
	 * @static
	 */
	connectedCallback () {
		super.connectedCallback();
		this.bopisStoreUnsubscribe = this.controller.store.subscribe(() => this.requestUpdate());

		this.addEventListener('click', this.handleClick);
		this.addEventListener('keypress', this.handleKeypress);
		this.modal.addEventListener('close', this.handleModalClose);
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		this.bopisStoreUnsubscribe();
		this.removeEventListener('click', this.handleClick);
		this.removeEventListener('keypress', this.handleKeypress);
		this.modal.removeEventListener('close', this.handleModalClose);

		super.disconnectedCallback();
	}

	get formattedAriaLabel () {
		if (this.controller?.store?.state?.myStore?.name) {
			return 'My Store: ' + this.currentStore.name;
		}

		return 'Select Your Store';
	}

	get currentStore () {
		if (this.controller?.store?.state?.myStore?.name) {
			return this.controller.store.state.myStore;
		}

		return null;
	}

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	mobileMenuTemplate () {
		return html`
			<span class="g-my-store-link g-my-store-link--mobile">
				<span class="u-icon-location g-my-store-link--location-icon" aria-hidden="true"></span>
				<span class="g-my-store-link--details">
					${this.currentStore?.name && !window.mivaJS.Is_Single_Store_Site ? html`<span class="g-my-store-link--detail-subtitle">Your Store</span>` : nothing}
					<span class="g-my-store-link--details-store">
						<span class="g-my-store-link--details-store-name">${this.currentStore?.name || 'Select Your Store'}</span>
						${window.mivaJS.Is_Single_Store_Site ? nothing : html`<span class="u-icon-chevron-down g-my-store-link--chevron-icon" aria-hidden="true"></span>`}
					</span>
					${window.mivaJS.Is_Single_Store_Site ? nothing : html`<span class="g-my-store-link--details-change">${this.currentStore?.name ? 'Change Location' : 'Select a Store'} </span>`}
				</span>
			</span>
		`;
	}

	headerTemplate () {
		return html`
			<span class="g-my-store-link g-my-store-link--header">
				<span class="u-icon-location g-my-store-link--location-icon" aria-hidden="true"></span>
				<span class="g-my-store-link--details">
					${this.currentStore?.properties.name && !window.mivaJS.Is_Single_Store_Site ? html`<span class="g-my-store-link--detail-subtitle">Your Store</span>` : nothing}
					<span class="g-my-store-link--details-store">
						<span class="g-my-store-link--details-store-name">${this.currentStore?.properties.name || 'Select Your Store'}</span>
						${window.mivaJS.Is_Single_Store_Site ? nothing : html`<span class="u-icon-chevron-down g-my-store-link--chevron-icon" aria-hidden="true"></span>`}
					</span>
				</span>
			</span>
		`;
	}

	render () {
		this.ariaLabel = this.formattedAriaLabel;
		this.title = this.formattedAriaLabel;

		return this.classList.contains('g-site-header__mystore')
			? html`${this.headerTemplate()}`
			: html`${this.mobileMenuTemplate()}`;
	}

	/**
	 * Handles the click on the button. This will open the modal with the BOPIS Drawer.
	 */
	handleClick = () => {
		// eslint-disable-next-line no-invalid-this
		this.myStoreClickLowLevel();
	}

	handleKeypress = (e) => {
		if (e.keyCode === 13) {
			// eslint-disable-next-line no-invalid-this
			this.myStoreClickLowLevel();
		}
	}

	myStoreClickLowLevel () {
		if (this.isSingleStoreSite && this.singleStoreUrl) {
			window.location.href = this.singleStoreUrl;
		} else {
			this.modal.open();
			this.ariaExpanded = true;
		}
	}

	/**
	 * Handles the modal's close event
	 */
	handleModalClose = () => {
		// eslint-disable-next-line no-invalid-this
		this.ariaExpanded = false;
	}

};

customElements.define('x-my-store', MyStore);
